import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import _find from 'lodash/find';
import _compact from 'lodash/compact';

import {
  ComposableMap,
  Geographies,
  Geography,
  Markers,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps';

import { geoAzimuthalEqualArea } from 'd3-geo';
import MapTooltip from './MapTooltip';
import { MARKER_COUNTRIES } from './constants';

const wrapperStyles = {
  width: '100%',
  maxWidth: 1000,
  marginLeft: 'auto',
};

class MarkerText extends Component {
  onMarkerEnter = (evt) => {
    const { onMarkerMouseEnter, country } = this.props;
    onMarkerMouseEnter(evt, country);
  };

  onMarkerLeave = (evt) => {
    const { onMarkerMouseLeave } = this.props;
    onMarkerMouseLeave(evt);
  };

  render() {
    const { textAnchor, style, country } = this.props;
    return (
      <Fragment>
        <g opacity="1">
          <text textAnchor={textAnchor} style={style}>
            {country.twoLetterCode}
          </text>
          <circle
            cx={0}
            cy={-7}
            r={14}
            style={{
              fill: 'white',
              fillOpacity: '0.0',
            }}
            onMouseOver={this.onMarkerEnter}
            onMouseLeave={this.onMarkerLeave}
          />
        </g>
      </Fragment>
    );
  }
}

class MapOfEurope extends Component {
  state = {
    tooltip: {
      show: false,
      left: 0,
      top: 0,
    },
  };

  projection(width, height, config) {
    return geoAzimuthalEqualArea()
      .rotate([-10, -52, 0])
      .scale(config.scale)
      .clipExtent([[0, 0], [width, height]])
      .translate([width / 2, height / 2]);
  }

  getCountryCode(geography) {
    return geography.properties.ADM0_A3;
  }

  getCountry(countryCode) {
    const { countries } = this.props.store.state;

    return (
      _find(
        countries,
        (country) => countryCode === country.threeLetterCode,
      ) || { isSchengen: false, isEU: false }
    );
  }

  fillColor(geography, isHover) {
    const countryCode = this.getCountryCode(geography);
    const country = this.getCountry(countryCode);
    const { fillColor } = this.props;
    let color = '#909091';

    if(fillColor !== null) {
      color = fillColor(country, isHover);
    }

    return color;
  }

  handleMove(geography, evt) {
    const { language } = this.props;
    const x = evt.clientX;
    const y = evt.clientY + window.pageYOffset;
    const newCountryCode = this.getCountryCode(geography);

    this.setState((prevState, props) => {
      const { tooltip } = prevState;
      const { countryCode } = { tooltip };

      if(newCountryCode !== countryCode) {
        const country = this.getCountry(newCountryCode);
        return {
          tooltip: {
            show: country.name ? true : false,
            left: x,
            top: y,
            countryCode: newCountryCode,
            title: country.name ? country.name[language] : null,
            subtitle: country.name ? country.name[language] : null,
            el: evt.target,
            isSchengen: country.isSchengen ? 'Schengen' : false,
          },
        };
      }
      else {
        return {
          tooltip: Object.assign({}, tooltip, { left: x, top: y }),
        };
      }
    });
  }

  handleLeave = () => {
    this.setState({
      tooltip: {
        show: false,
      },
    });
  };

  handleMarkerEnter = (evt, country) => {
    const { language } = this.props;
    const x = evt.clientX;
    const y = evt.clientY + window.pageYOffset;

    this.setState((prevState, props) => ({
      tooltip: {
        show: !prevState.tooltip.show,
        left: x,
        top: y,
        title: country.name ? country.name[language] : null,
        isSchengen: country.isSchengen ? 'Schengen' : null,
      },
    }));
  };

  rednderMarkers(countries) {
    const markers = countries.map((country, index) => {
      let marker = null;
      if(MARKER_COUNTRIES.indexOf(country.threeLetterCode.toString()) !== -1) {
        const coordinates = [country.lng, country.lat];
        marker = (
          <Marker key={index.toString()} marker={{ coordinates }}>
            <MarkerText
              textAnchor="middle"
              style={{
                fontFamily: 'Quicksand, sans-serif',
                fill: country.isSchengen ? '#c9a874' : '#ffffff',
                fontSize: '10px',
                fontWeight: '700',
              }}
              onMarkerMouseEnter={this.handleMarkerEnter}
              onMarkerMouseLeave={this.handleLeave}
              country={country}
            />
          </Marker>
        );
      }
      return marker;
    });

    return _compact(markers);
  }

  render() {
    const { width } = this.props;
    const height = width * 0.9;
    const scale = width * 1.4;
    const { tooltip } = this.state;
    const { countries } = this.props.store.state;

    return (
      <div style={wrapperStyles}>
        <ComposableMap
          projection={this.projection}
          projectionConfig={{
            scale: scale,
          }}
          width={width}
          height={height}
          style={{
            width: width,
            height: height,
          }}
        >
          <ZoomableGroup center={[10, 52]} zoom={1} disablePanning>
            <Geographies geography="/maps/europe.json">
              {(geographies, projection) => {
                return geographies.map((geography, i) => (
                  <Geography
                    key={`geo-${i}`}
                    cacheId={`geo-${i}`}
                    geography={geography}
                    projection={projection}
                    onMouseMove={this.handleMove.bind(this)}
                    onMouseLeave={this.handleLeave}
                    style={{
                      default: {
                        fill: this.fillColor(geography, false),
                        stroke: '#FFFFFF',
                        strokeWidth: 0.75,
                        outline: 'none',
                      },
                      hover: {
                        fill: this.fillColor(geography, true),
                        stroke: '#FFFFFF',
                        strokeWidth: 0.75,
                        outline: 'none',
                      },
                      pressed: {
                        fill: this.fillColor(geography, true),
                        stroke: '#FFFFFF',
                        strokeWidth: 0.75,
                        outline: 'none',
                      },
                    }}
                  />
                ));
              }}
            </Geographies>
            <Markers>{this.rednderMarkers(countries)}</Markers>
          </ZoomableGroup>
        </ComposableMap>
        <MapTooltip {...tooltip} />
      </div>
    );
  }
}

MapOfEurope.propTypes = {
  width: PropTypes.number.isRequired,
  fillColor: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.shape({
        cz: PropTypes.string,
        en: PropTypes.string,
        ru: PropTypes.string,
      }),
      threeLetterCode: PropTypes.string,
    }),
  ),
};

export default MapOfEurope;



// WEBPACK FOOTER //
// ./src/maps/MapOfEurope.js